import React, { useState, useEffect, useMemo } from 'react';
import { getLitrosPorProduto } from '../../services/api';
import { formatarLitros } from '../../utils/formatters';
import { serializarFiltros } from '../../utils/compararFiltros';

const LitrosVendidosCombustivel = ({ filtros = {}, loading: loadingProp, onCrossFilter, filtroCruzadoAtivo }) => {
  const [dadosAtual, setDadosAtual] = useState([]);
  const [dadosAnterior, setDadosAnterior] = useState([]);
  const [loading, setLoading] = useState(true);

  // Função para calcular o mês anterior completo baseado nos filtros
  const calcularMesAnteriorCompleto = (filtros) => {
    const filtroData = filtros['visao_geral.data_hora_abastecimento_sp_date'];
    if (!filtroData) return null;

    let dataInicio, dataFim;
    if (filtroData.includes(' to ')) {
      [dataInicio, dataFim] = filtroData.split(' to ').map(d => d.trim());
    } else {
      dataInicio = dataFim = filtroData;
    }

    // Pegar a data de início para determinar o mês
    const dataAtual = new Date(dataInicio + 'T00:00:00');

    // Calcular o mês anterior completo
    const mesAnterior = new Date(dataAtual);
    mesAnterior.setMonth(mesAnterior.getMonth() - 1);

    // Primeiro dia do mês anterior
    const inicioAnterior = new Date(mesAnterior.getFullYear(), mesAnterior.getMonth(), 1);

    // Último dia do mês anterior
    const fimAnterior = new Date(mesAnterior.getFullYear(), mesAnterior.getMonth() + 1, 0);

    return {
      inicio: inicioAnterior.toISOString().split('T')[0],
      fim: fimAnterior.toISOString().split('T')[0],
    };
  };

  // Memoizar a serialização dos filtros para evitar chamadas desnecessárias
  const filtrosSerializados = useMemo(() => serializarFiltros(filtros), [filtros]);

  useEffect(() => {
    const fetchDados = async () => {
      if (loadingProp) {
        setLoading(true);
        return;
      }

      setLoading(true);
      try {
        // Buscar dados do período atual
        const resultadoAtual = await getLitrosPorProduto(filtros);
        const produtosAtual = resultadoAtual.map(item => ({
          produto: item['visao_geral.nome_produto'] || item.nome_produto || '-',
          quantidade: parseFloat(item['visao_geral.quantidade'] || item.quantidade || 0),
        })).filter(p => p.quantidade > 0);

        setDadosAtual(produtosAtual);

        // Buscar dados do mês anterior completo
        const mesAnterior = calcularMesAnteriorCompleto(filtros);
        if (mesAnterior) {
          const filtrosAnterior = {
            ...filtros,
            'visao_geral.data_hora_abastecimento_sp_date': `${mesAnterior.inicio} to ${mesAnterior.fim}`,
          };

          const resultadoAnterior = await getLitrosPorProduto(filtrosAnterior);
          const produtosAnterior = resultadoAnterior.map(item => ({
            produto: item['visao_geral.nome_produto'] || item.nome_produto || '-',
            quantidade: parseFloat(item['visao_geral.quantidade'] || item.quantidade || 0),
          })).filter(p => p.quantidade > 0);

          setDadosAnterior(produtosAnterior);
        } else {
          setDadosAnterior([]);
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        setDadosAtual([]);
        setDadosAnterior([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDados();
  }, [filtrosSerializados, loadingProp]);

  // Combinar dados de ambos os períodos
  const dadosCombinados = useMemo(() => {
    // Obter todos os produtos únicos de ambos os períodos
    const todosProdutos = new Set();
    dadosAtual.forEach(d => todosProdutos.add(d.produto));
    dadosAnterior.forEach(d => todosProdutos.add(d.produto));

    // Combinar dados para cada produto
    const combinados = Array.from(todosProdutos).map(produto => {
      const atual = dadosAtual.find(d => d.produto === produto);
      const anterior = dadosAnterior.find(d => d.produto === produto);

      const quantidadeAtual = atual?.quantidade || 0;
      const quantidadeAnterior = anterior?.quantidade || 0;

      // Calcular percentual de variação
      const variacao = quantidadeAnterior > 0
        ? ((quantidadeAtual - quantidadeAnterior) / quantidadeAnterior) * 100
        : quantidadeAtual > 0 ? 100 : 0;

      return {
        produto,
        quantidadeAtual,
        quantidadeAnterior,
        variacao,
      };
    }).filter(d => d.quantidadeAtual > 0 || d.quantidadeAnterior > 0);

    // Ordenar por quantidade atual (maior para menor)
    combinados.sort((a, b) => b.quantidadeAtual - a.quantidadeAtual);

    return combinados;
  }, [dadosAtual, dadosAnterior]);

  // Encontrar o máximo para normalizar as barras
  const maxValor = useMemo(() => {
    const todosValores = dadosCombinados.flatMap(d => [d.quantidadeAtual, d.quantidadeAnterior]);
    return Math.max(...todosValores, 1);
  }, [dadosCombinados]);

  // Formatar valor para exibição nas barras (com pontos como separadores)
  const formatarValorBarra = (valor) => {
    if (!valor || valor === 0) return '0 L';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(valor) + ' L';
  };

  // Verificar se há espaço suficiente na barra para exibir o valor dentro
  const temEspacoNaBarra = (percentualLargura, valor) => {
    // Se a barra for menor que 40%, não coloca o valor dentro (threshold mais conservador)
    if (percentualLargura < 40) return false;

    // Estimar tamanho do texto formatado
    const textoFormatado = formatarValorBarra(valor);
    const numCaracteres = textoFormatado.length;

    // Valores muito longos (mais de 12 caracteres) precisam de mais espaço
    if (numCaracteres > 12 && percentualLargura < 50) return false;
    if (numCaracteres > 15 && percentualLargura < 60) return false;

    return true;
  };

  // Verificar se um produto está selecionado
  const isProdutoSelected = (produto) => {
    return filtroCruzadoAtivo === produto;
  };

  // Handler para clique no produto
  const handleProdutoClick = (produto) => {
    if (onCrossFilter) {
      onCrossFilter(produto);
    }
  };

  return (
    <div className="dashboard-card">
      {/* Header */}
      <div className="flex flex-col items-start flex-shrink-0">
        <h3 className="dashboard-card-header">Litros por combustível</h3>
        <div className="dashboard-card-divider w-full"></div>
      </div>

      {loading ? (
        <div className="fluid-spinner-container flex-1">
          <div className="flex flex-col items-center" style={{ gap: 'var(--space-fluid-sm)' }}>
            <div className="fluid-spinner"></div>
            <span className="fluid-spinner-text">Carregando dados...</span>
          </div>
        </div>
      ) : dadosCombinados.length > 0 ? (
        <>
          {/* Lista de combustíveis com scroll */}
          <div className="overflow-y-auto flex-1">
            {dadosCombinados.map((item, index) => (
              <div
                key={index}
                onClick={() => handleProdutoClick(item.produto)}
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                title={`Clique para filtrar por ${item.produto}`}
              >
                <div className="flex items-center border-t border-gray-200" style={{ gap: 'var(--space-fluid-sm)', paddingTop: 'var(--space-fluid-xs)' }}>
                  <div className="flex-shrink-0 text-right w-1/3">
                    <span className="font-medium text-gray-800 truncate block" style={{ fontSize: 'var(--text-fluid-xs)' }}>{item.produto}</span>
                  </div>
                  <div className="flex-1" style={{ display: 'flex', flexDirection: 'column', gap: 'calc(var(--space-fluid-xs) * 0.5)' }}>
                    <div className="flex items-center fluid-progress-bar-sm" style={{ gap: 'var(--space-fluid-xs)' }}>
                      <div
                        className="h-full rounded-r-full bg-[#5F86A8] flex items-center transition-all duration-300 overflow-hidden"
                        style={{ width: `${(item.quantidadeAtual / maxValor) * 100}%` }}
                      >
                        {temEspacoNaBarra((item.quantidadeAtual / maxValor) * 100, item.quantidadeAtual) && (
                          <span className="font-bold text-white whitespace-nowrap" style={{ fontSize: 'var(--text-fluid-xs)', padding: '0 var(--space-fluid-xs)' }}>
                            {formatarValorBarra(item.quantidadeAtual)}
                          </span>
                        )}
                      </div>
                      {!temEspacoNaBarra((item.quantidadeAtual / maxValor) * 100, item.quantidadeAtual) && (
                        <span className="font-bold text-gray-700 whitespace-nowrap flex-shrink-0" style={{ fontSize: 'var(--text-fluid-xs)' }}>
                          {formatarValorBarra(item.quantidadeAtual)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center fluid-progress-bar-sm" style={{ gap: 'var(--space-fluid-xs)' }}>
                      <div
                        className="h-full rounded-r-full bg-[#A7BDD2] flex items-center transition-all duration-300 overflow-hidden"
                        style={{ width: `${(item.quantidadeAnterior / maxValor) * 100}%` }}
                      >
                        {temEspacoNaBarra((item.quantidadeAnterior / maxValor) * 100, item.quantidadeAnterior) && (
                          <span className="font-bold text-white whitespace-nowrap" style={{ fontSize: 'var(--text-fluid-xs)', padding: '0 var(--space-fluid-xs)' }}>
                            {formatarValorBarra(item.quantidadeAnterior)}
                          </span>
                        )}
                      </div>
                      {!temEspacoNaBarra((item.quantidadeAnterior / maxValor) * 100, item.quantidadeAnterior) && (
                        <span className="font-bold text-gray-700 whitespace-nowrap flex-shrink-0" style={{ fontSize: 'var(--text-fluid-xs)' }}>
                          {formatarValorBarra(item.quantidadeAnterior)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-start" style={{ gap: 'var(--space-fluid-xs)', marginTop: 'var(--space-fluid-xs)' }}>
                  <span className={item.variacao >= 0 ? 'text-green-500' : 'text-red-500'} style={{ position: 'relative', top: '-2px' }}>
                    {item.variacao >= 0 ? '▲' : '▼'}
                  </span>
                  <span className={item.variacao >= 0 ? 'text-green-500' : 'text-red-500'} style={{ fontSize: 'var(--text-fluid-xs)' }}>
                    {Math.abs(item.variacao).toFixed(0)}% {item.variacao >= 0 ? 'faturamento maior que o mês o anterior' : 'faturamento menor que o mês o anterior'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Legenda */}
          <div className="flex flex-shrink-0 justify-start border-t border-gray-200" style={{ gap: 'var(--space-fluid-xs)', marginTop: 'var(--space-fluid-xs)', paddingTop: 'var(--space-fluid-xs)' }}>
            <div className="fluid-legend">
              <span className="fluid-legend-dot bg-[#5F86A8]"></span>
              <span className="fluid-legend-text">Atual</span>
            </div>
            <div className="fluid-legend">
              <span className="fluid-legend-dot bg-[#A7BDD2]"></span>
              <span className="fluid-legend-text">Anterior</span>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full flex items-center justify-center flex-1 text-gray-500" style={{ padding: 'var(--space-fluid-lg)', fontSize: 'var(--text-fluid-sm)' }}>
          Nenhum dado disponível
        </div>
      )}
    </div>
  );
};

export default LitrosVendidosCombustivel;

